import { useState } from "react";
import { motion } from "framer-motion";
import { Bot, Info } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { cn, getModelThumbnailUrl } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { LLMModelTooltipContent } from "./LLMModelTooltipContent";
import type { LLMModel } from "@/api/models/api-types";

interface LLMModelCardProps {
  model: LLMModel;
  isSelected?: boolean;
  onSelect: (model: LLMModel) => void;
  isMobileCompact?: boolean; // 🆕 Mobilde dialog için kompakt horizontal layout
}

export const LLMModelCard = ({
  model,
  isSelected = false,
  onSelect,
  isMobileCompact = false,
}: LLMModelCardProps) => {
  const [imageError, setImageError] = useState(false);
  const { i18n, t } = useTranslation();

  const handleImageError = () => {
    setImageError(true);
  };

  // Multi-language description desteği
  const getCurrentDescription = () => {
    if (!model.description) return "";
    const currentLang = i18n.resolvedLanguage || "en";
    const found = model.description[
      currentLang as keyof typeof model.description
    ] as string;
    return found || model.description.en || "";
  };

  const description = getCurrentDescription();

  // Get thumbnail URL (R2 veya thumbnail_url)
  const thumbnailUrl = getModelThumbnailUrl(model);

  // Card content
  const cardContent = (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: isMobileCompact ? 1 : 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2 }}
      className={cn(
        "bg-bg-secondary dark:bg-gray-800 rounded-2xl transition-all duration-200",
        "cursor-pointer transition-all duration-300",
        "hover:shadow-gradient-harmoni-hover",
        isSelected
          ? "border-gradient-harmoni bg-gradient-to-br from-primary/10 to-secondary/10 shadow-gradient-harmoni"
          : "hover:border-primary/30",
        isMobileCompact
          ? "flex flex-row p-3 gap-3 items-center" // Mobil kompakt: horizontal
          : "h-full flex flex-col p-3" // Normal: vertical
      )}
      data-model-card
      onClick={() => onSelect(model)}
    >
      {/* Fixed aspect ratio container */}
      <div
        className={cn(
          "rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 relative flex-shrink-0",
          isMobileCompact
            ? "w-20 h-20" // Mobil kompakt: küçük kare thumbnail
            : "aspect-square mb-3" // Normal: tam genişlik
        )}
      >
        {thumbnailUrl && !imageError ? (
          <img
            src={thumbnailUrl}
            alt={model.title}
            className="w-full h-full object-cover transition-opacity duration-300"
            loading="lazy"
            onError={handleImageError}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800">
            <Bot className="w-8 h-8 mb-2" />
            <span className="text-xs text-center px-2">
              {t("llmSelection.llmModel")}
            </span>
          </div>
        )}

        {/* Price overlay - sağ üst */}
        <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-md font-medium price-tag">
          {model.price
            ? model.price.toFixed(3)
            : `${(model.pricing?.inputTokenPrice ?? 0).toFixed(3)}/${(
                model.pricing?.outputTokenPrice ?? 0
              ).toFixed(3)}`}
        </div>

        {/* Info button - mobilde göster */}
        <button
          className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white p-1.5 rounded-md hover:bg-black/80 transition-colors md:hidden"
          onClick={(e) => {
            e.stopPropagation();
            // Info action için
          }}
          title={t("llmSelection.modelInfo")}
        >
          <Info className="w-3 h-3" />
        </button>
      </div>

      {/* Text content - flex-grow for consistent heights */}
      <div
        className={cn(
          "flex-grow flex flex-col",
          isMobileCompact ? "gap-1 justify-center" : "justify-between"
        )}
      >
        <div
          className={cn(
            "font-medium text-gray-900 dark:text-white leading-tight",
            isMobileCompact
              ? "text-sm line-clamp-1 mb-0.5" // Mobil kompakt: 1 satır + az margin
              : "text-sm mb-1 line-clamp-2" // Normal: 2 satır
          )}
        >
          {model.title}
        </div>
        <div
          className={cn(
            "text-gray-500 dark:text-gray-400",
            isMobileCompact
              ? "text-[11px] line-clamp-2 mb-1" // Mobil: küçük text, 2 satır
              : "text-xs line-clamp-1 mb-2" // Normal: 1 satır
          )}
        >
          {description}
        </div>

        {/* Provider name at bottom */}
        <div
          className={cn(
            "text-xs text-gray-500 dark:text-gray-400",
            isMobileCompact ? "" : "mt-auto"
          )}
        >
          <div className={isMobileCompact ? "" : "mb-1"}>
            {model.provider?.name || t("llmSelection.unknown")}
          </div>
        </div>
      </div>
    </motion.div>
  );

  // Tooltip'i aktif et
  return (
    <>
      {/* Desktop: Tooltip */}
      <div className="hidden md:block h-full">
        <Tooltip.Root delayDuration={200}>
          <Tooltip.Trigger asChild>{cardContent}</Tooltip.Trigger>
          <Tooltip.Portal>
            <LLMModelTooltipContent model={model} />
          </Tooltip.Portal>
        </Tooltip.Root>
      </div>

      {/* Mobile: Sadece kart */}
      <div className="md:hidden h-full">{cardContent}</div>
    </>
  );
};
